import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

// ── API helpers ───────────────────────────────────────────────────────────────
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('ep_token')}` });
const get  = (url)       => axios.get(url,       { headers: authHeader() }).then(r => r.data);
const put  = (url, data) => axios.put(url, data,  { headers: authHeader() }).then(r => r.data);
const post = (url, data) => axios.post(url, data, { headers: authHeader() }).then(r => r.data);

// ── Avatar ────────────────────────────────────────────────────────────────────
const ROLE_COLORS = {
  super_admin:  '#7c3aed', dealer: '#0f766e', photographer: '#1d4ed8',
  event_owner: '#b45309', visitor: '#be123c',
};
const ROLE_LABEL = {
  super_admin: 'Super Admin', dealer: 'Dealer', photographer: 'Photographer',
  event_owner: 'Event Owner', visitor: 'Visitor',
};

function Avatar({ user, size = 36 }) {
  const [err, setErr] = useState(false);
  const name = [user?.surname, user?.name].filter(Boolean).join(' ') || user?.full_name || user?.name || '?';
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const color = ROLE_COLORS[user?.role] || '#6b7280';

  if (user?.profile_photo && !err) {
    return <img src={user.profile_photo} alt={initials} onError={() => setErr(true)}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${color}` }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.36, fontWeight: 800, flexShrink: 0, letterSpacing: '-0.5px', border: `2px solid ${color}` }}>
      {initials}
    </div>
  );
}

function RolePill({ role, small }) {
  const color = ROLE_COLORS[role] || '#6b7280';
  return (
    <span style={{ background: color + '20', color, padding: small ? '1px 6px' : '2px 9px', borderRadius: 10, fontSize: small ? 10 : 11, fontWeight: 700, whiteSpace: 'nowrap', border: `1px solid ${color}40` }}>
      {ROLE_LABEL[role] || role}
    </span>
  );
}

// ── Compact Edit Form ─────────────────────────────────────────────────────────
function EditForm({ user, onSaved, compact }) {
  const [form, setForm]   = useState({ ...user });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]     = useState('');
  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inp = (field, opts = {}) => (
    <input value={form[field] ?? ''} onChange={e => s(field, e.target.value)}
      style={{ padding: '4px 7px', border: '1.5px solid #d1d5db', borderRadius: 5, fontSize: 12, width: '100%', boxSizing: 'border-box', outline: 'none', ...opts.style }}
      {...opts} />
  );

  const F = ({ label, w, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: w, minWidth: 0 }}>
      <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      {children}
    </div>
  );

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await put(`/api/superadmin/users/${form.id}`, form);
      setMsg('✓ Saved'); onSaved && onSaved(form);
    } catch (e) { setMsg(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      {/* header */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 10, background: '#f9fafb', borderRadius: '8px 8px 0 0' }}>
        <Avatar user={form} size={32} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#111' }}>{[form.surname, form.name].filter(Boolean).join(' ') || 'User'}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>{form.mobile_number}</div>
        </div>
        <RolePill role={form.role} />
        {msg && <span style={{ fontSize: 11, color: msg.startsWith('✓') ? '#15803d' : '#dc2626' }}>{msg}</span>}
        <button onClick={save} disabled={saving} style={{ padding: '4px 14px', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? '…' : 'Save'}
        </button>
      </div>

      {/* body */}
      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <F label="Surname"         w={100}>{inp('surname')}</F>
          <F label="Name"            w={100}>{inp('name')}</F>
          <F label="Father/Husband"  w={140}>{inp('father_husband')}</F>
          <F label="ISD"             w={48}>{inp('isd', { maxLength: 5 })}</F>
          <F label="DOB"             w={120}>{inp('dob', { type: 'date' })}</F>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <F label="Mobile (readonly)" w={120}>
            <input value={form.mobile_number || ''} disabled style={{ padding: '4px 7px', border: '1.5px solid #e5e7eb', borderRadius: 5, fontSize: 12, background: '#f9fafb', color: '#6b7280', width: '100%', boxSizing: 'border-box' }} />
          </F>
          <F label="Email"    w={180}>{inp('email', { type: 'email' })}</F>
          <F label="City"     w={100}>{inp('city')}</F>
          <F label="Pincode"  w={76}>{inp('pincode', { maxLength: 6 })}</F>
          <F label="State"    w={120}>{inp('state')}</F>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <F label="Address 1" w={200}>{inp('address1')}</F>
          <F label="Address 2" w={200}>{inp('address2')}</F>
          <F label="District"  w={120}>{inp('district')}</F>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <F label="Spouse Name"   w={140}>{inp('spouce_name')}</F>
          <F label="Spouse Mobile" w={110}>{inp('spouce_mobile', { maxLength: 10 })}</F>
          <F label="Referred By"   w={130}>{inp('refered_by')}</F>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {form.role === 'dealer' && (
            <F label="Event Limit" w={100}>
              <input type="number" min={0} value={form.event_limit ?? 0} onChange={e => s('event_limit', +e.target.value)}
                style={{ padding: '4px 7px', border: '1.5px solid #d1d5db', borderRadius: 5, fontSize: 12, width: '100%', boxSizing: 'border-box' }} />
            </F>
          )}
          {(form.role === 'dealer' || form.role === 'photographer') && (
            <F label="Remaining Quota" w={110}>
              <input type="number" min={0} value={form.remaining_quota ?? 0} onChange={e => s('remaining_quota', +e.target.value)}
                style={{ padding: '4px 7px', border: '1.5px solid #d1d5db', borderRadius: 5, fontSize: 12, width: '100%', boxSizing: 'border-box' }} />
            </F>
          )}
          <F label="New Password (4 digits)" w={150}>
            <input type="password" maxLength={4} placeholder="Leave blank to keep" value={form.password || ''}
              onChange={e => s('password', e.target.value.replace(/\D/g, '').slice(0, 4))}
              style={{ padding: '4px 7px', border: '1.5px solid #d1d5db', borderRadius: 5, fontSize: 12, width: '100%', boxSizing: 'border-box' }} />
          </F>
          <F label="Active" w={50}>
            <div style={{ height: 28, display: 'flex', alignItems: 'center' }}>
              <input type="checkbox" checked={!!form.is_active} onChange={e => s('is_active', e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }} />
            </div>
          </F>
        </div>
      </div>
    </div>
  );
}

// ── New User Form ─────────────────────────────────────────────────────────────
function NewUserForm({ role, onSaved, onCancel }) {
  const [form, setForm] = useState({ role, isd: '91' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]   = useState('');
  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = (field, opts = {}) => (
    <input value={form[field] || ''} onChange={e => s(field, e.target.value)}
      style={{ padding: '4px 7px', border: '1.5px solid #d1d5db', borderRadius: 5, fontSize: 12, width: '100%', boxSizing: 'border-box' }} {...opts} />
  );
  const F = ({ label, w, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: w }}>
      <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>{label}</span>
      {children}
    </div>
  );
  const save = async () => {
    if (!form.mobile_number || form.mobile_number.length !== 10) { setMsg('10-digit mobile required'); return; }
    setSaving(true); setMsg('');
    try { await post('/api/superadmin/users', form); onSaved && onSaved(); }
    catch (e) { setMsg(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div style={{ background: '#fff', border: `2px solid ${ROLE_COLORS[role]}40`, borderRadius: 8 }}>
      <div style={{ padding: '7px 12px', background: ROLE_COLORS[role] + '15', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '6px 6px 0 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>New</span>
          <RolePill role={role} />
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {msg && <span style={{ fontSize: 11, color: '#dc2626' }}>{msg}</span>}
          <button onClick={onCancel} style={{ padding: '3px 10px', background: '#f3f4f6', border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding: '3px 10px', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{saving ? '…' : 'Create'}</button>
        </div>
      </div>
      <div style={{ padding: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <F label="Surname" w={90}>{inp('surname')}</F>
        <F label="Name" w={90}>{inp('name')}</F>
        <F label="Mobile *" w={110}>{inp('mobile_number', { maxLength: 10 })}</F>
        <F label="Email" w={160}>{inp('email', { type: 'email' })}</F>
        <F label="City" w={90}>{inp('city')}</F>
        <F label="Password" w={90}>
          <input type="password" maxLength={4} placeholder="4 digits" value={form.password || ''}
            onChange={e => s('password', e.target.value.replace(/\D/g, '').slice(0, 4))}
            style={{ padding: '4px 7px', border: '1.5px solid #d1d5db', borderRadius: 5, fontSize: 12, width: '100%', boxSizing: 'border-box' }} />
        </F>
      </div>
    </div>
  );
}

// ── User Card (compact list item) ──────────────────────────────────────────────
function UserCard({ user, isSelected, onClick, badge }) {
  const name = [user.surname, user.name].filter(Boolean).join(' ') || user.full_name || user.name || `#${user.id}`;
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', cursor: 'pointer',
      borderBottom: '1px solid #f3f4f6', background: isSelected ? '#eff6ff' : '#fff',
      borderLeft: isSelected ? `3px solid ${ROLE_COLORS[user.role] || '#2563eb'}` : '3px solid transparent',
      transition: 'background 0.1s',
    }}>
      <Avatar user={user} size={30} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 12, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
        <div style={{ fontSize: 10, color: '#9ca3af', display: 'flex', gap: 6 }}>
          <span>{user.mobile_number}</span>
          {badge && <span style={{ color: '#4338ca', fontWeight: 700 }}>{badge}</span>}
        </div>
      </div>
      {!user.is_active && <span style={{ fontSize: 9, background: '#fef2f2', color: '#dc2626', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>OFF</span>}
      <span style={{ fontSize: 16, color: '#d1d5db' }}>›</span>
    </div>
  );
}

// ── Event Card ─────────────────────────────────────────────────────────────────
function EventCard({ event, isSelected, onClick }) {
  const statusColor = { active: '#1d4ed8', completed: '#15803d', published: '#7c3aed', matching: '#b45309' };
  return (
    <div onClick={onClick} style={{
      padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
      background: isSelected ? '#fdf4ff' : '#fff',
      borderLeft: isSelected ? '3px solid #7c3aed' : '3px solid transparent',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <div style={{ fontWeight: 600, fontSize: 12, color: '#111', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.name}</div>
        <span style={{ fontSize: 9, background: (statusColor[event.status] || '#6b7280') + '20', color: statusColor[event.status] || '#6b7280', padding: '1px 6px', borderRadius: 8, fontWeight: 700, textTransform: 'uppercase', flexShrink: 0 }}>{event.status}</span>
      </div>
      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2, display: 'flex', gap: 6 }}>
        <span>{event.date ? new Date(event.date).toLocaleDateString('en-IN') : '—'}</span>
        {event.photographer_name && <span>· {event.photographer_name}</span>}
      </div>
    </div>
  );
}

// ── Visitors Table ─────────────────────────────────────────────────────────────
function VisitorsTable({ visitors, loading, onSelect, selectedId }) {
  if (loading) return <div style={{ padding: 12, fontSize: 12, color: '#9ca3af' }}>Loading visitors…</div>;
  if (!visitors.length) return <div style={{ padding: 12, fontSize: 12, color: '#9ca3af' }}>No visitors registered</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
            {['#','','Name','Mobile','Registered','Photos',''].map((h,i) => (
              <th key={i} style={{ padding: '5px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visitors.map((v, i) => {
            const name = v.full_name || [v.surname, v.name].filter(Boolean).join(' ') || '—';
            return (
              <tr key={v.id} onClick={() => onSelect(v)} style={{ background: selectedId === v.id ? '#fef2f2' : i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}>
                <td style={{ padding: '5px 8px', color: '#9ca3af', fontSize: 11 }}>{i + 1}</td>
                <td style={{ padding: '5px 4px' }}><Avatar user={{ ...v, role: 'visitor' }} size={24} /></td>
                <td style={{ padding: '5px 8px', fontWeight: 600 }}>{name}</td>
                <td style={{ padding: '5px 8px', color: '#6b7280' }}>{v.mobile_number}</td>
                <td style={{ padding: '5px 8px', color: '#6b7280', fontSize: 11 }}>{v.registered_at ? new Date(v.registered_at).toLocaleDateString('en-IN') : '—'}</td>
                <td style={{ padding: '5px 8px' }}>
                  <span style={{ background: v.match_count > 0 ? '#dcfce7' : '#f3f4f6', color: v.match_count > 0 ? '#15803d' : '#9ca3af', padding: '1px 7px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>{v.match_count}</span>
                </td>
                <td style={{ padding: '5px 8px' }}>
                  <span style={{ fontSize: 11, color: '#2563eb', fontWeight: 700, cursor: 'pointer' }} onClick={e => { e.stopPropagation(); onSelect(v); }}>Edit</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Section wrapper ────────────────────────────────────────────────────────────
function Section({ title, count, color, action, children }) {
  return (
    <div style={{ borderBottom: '2px solid #e5e7eb' }}>
      <div style={{ padding: '6px 10px', background: color || '#1a1a2e', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{title}</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {count !== undefined && <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: '1px 7px', fontSize: 10 }}>{count}</span>}
          {action}
        </div>
      </div>
      {children}
    </div>
  );
}

function AddBtn({ label, onClick }) {
  return (
    <button onClick={onClick} style={{ padding: '2px 8px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
      + {label}
    </button>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function SuperAdminUsersManagement() {
  // Navigation stack: each item = { level, data }
  // level: 'root' | 'dealer' | 'photographer' | 'event' | 'visitor'
  const [stack, setStack]         = useState([{ level: 'root', data: null }]);
  const [dealers, setDealers]     = useState([]);
  const [children, setChildren]   = useState({ photographers: [], events: [], visitors: [] });
  const [editUser, setEditUser]   = useState(null);
  const [newRole, setNewRole]     = useState(null);
  const [loading, setLoading]     = useState({});
  const panelRef = useRef(null);

  const current     = stack[stack.length - 1];
  const currentLevel = current.level;
  const currentData  = current.data;

  // Escape key goes back
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && stack.length > 1) goBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stack]);

  // Load based on current level
  useEffect(() => {
    setEditUser(null); setNewRole(null);
    if (currentLevel === 'root') {
      setLoading(l => ({ ...l, dealers: true }));
      get('/api/superadmin/users/dealers')
        .then(d => setDealers(d))
        .finally(() => setLoading(l => ({ ...l, dealers: false })));
    }
    if (currentLevel === 'dealer') {
      setLoading(l => ({ ...l, photographers: true, events: true }));
      Promise.all([
        get(`/api/superadmin/users/photographers?dealer_id=${currentData.id}`),
        get(`/api/superadmin/dealers/${currentData.id}/events`),
      ]).then(([p, e]) => setChildren(c => ({ ...c, photographers: p, events: e })))
        .finally(() => setLoading(l => ({ ...l, photographers: false, events: false })));
    }
    if (currentLevel === 'event') {
      setLoading(l => ({ ...l, visitors: true }));
      get(`/api/superadmin/events/${currentData.id}/visitors`)
        .then(v => setChildren(c => ({ ...c, visitors: v })))
        .finally(() => setLoading(l => ({ ...l, visitors: false })));
    }
  }, [currentLevel, currentData?.id]);

  const goBack = () => setStack(s => s.slice(0, -1));
  const pushLevel = (level, data) => {
    setStack(s => [...s, { level, data }]);
    setEditUser(null); setNewRole(null);
  };

  const reload = () => {
    // Re-trigger the effect by creating a new stack item copy
    setStack(s => [...s.slice(0, -1), { ...s[s.length - 1] }]);
  };

  // Panel width: left panel shows navigation, right shows form
  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 56px)', fontFamily: "'Segoe UI', system-ui, sans-serif", background: '#f1f5f9' }}>

      {/* ── LEFT NAVIGATION PANEL ── */}
      <div ref={panelRef} style={{ width: 260, flexShrink: 0, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff' }}>

        {/* Breadcrumb */}
        <div style={{ padding: '6px 10px', background: '#0f172a', borderBottom: '1px solid #1e293b' }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            {stack.map((item, i) => (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {i > 0 && <span style={{ color: '#475569', fontSize: 10 }}>›</span>}
                <button onClick={() => setStack(s => s.slice(0, i + 1))}
                  style={{ background: 'none', border: 'none', cursor: i < stack.length - 1 ? 'pointer' : 'default', color: i === stack.length - 1 ? '#f8fafc' : '#94a3b8', fontSize: 11, fontWeight: i === stack.length - 1 ? 700 : 400, padding: 0 }}>
                  {item.level === 'root' ? 'Users' :
                   item.level === 'event' ? (item.data?.name?.slice(0, 16) + (item.data?.name?.length > 16 ? '…' : '')) :
                   ([item.data?.surname, item.data?.name].filter(Boolean).join(' ') || `#${item.data?.id}`).slice(0, 16)}
                </button>
              </span>
            ))}
          </div>
          {stack.length > 1 && (
            <button onClick={goBack} style={{ marginTop: 4, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, color: '#94a3b8', fontSize: 10, cursor: 'pointer', padding: '2px 8px' }}>
              ← Back  <kbd style={{ fontSize: 9, opacity: 0.6 }}>Esc</kbd>
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>

          {/* ROOT → show dealers */}
          {currentLevel === 'root' && (
            <Section title="Dealers" count={dealers.length} color="#1a1a2e"
              action={<AddBtn label="Dealer" onClick={() => { setNewRole('dealer'); setEditUser(null); }} />}>
              {loading.dealers
                ? <div style={{ padding: 10, fontSize: 12, color: '#9ca3af' }}>Loading…</div>
                : dealers.map(d => (
                  <UserCard key={d.id} user={{ ...d, role: 'dealer' }}
                    isSelected={editUser?.id === d.id && !newRole}
                    onClick={() => { setEditUser({ ...d, role: 'dealer' }); setNewRole(null); }}
                    badge={`Q:${d.remaining_quota ?? 0}`} />
                ))
              }
              {dealers.map(d => (
                <div key={`drill-${d.id}`} onClick={() => pushLevel('dealer', { ...d, role: 'dealer' })}
                  style={{ padding: '4px 10px 4px 48px', fontSize: 11, color: '#2563eb', cursor: 'pointer', borderBottom: '1px solid #f9fafb', background: '#fafcff' }}>
                  ↳ Enter dealer space →
                </div>
              ))}
            </Section>
          )}

          {/* DEALER → show photographers + events */}
          {currentLevel === 'dealer' && (
            <>
              {/* Dealer summary card */}
              <div onClick={() => { setEditUser({ ...currentData, role: 'dealer' }); setNewRole(null); }}
                style={{ padding: '10px', background: ROLE_COLORS.dealer + '10', borderBottom: '2px solid #e5e7eb', cursor: 'pointer' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Avatar user={{ ...currentData, role: 'dealer' }} size={34} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{[currentData.surname, currentData.name].filter(Boolean).join(' ')}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{currentData.mobile_number}</div>
                  </div>
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: '#2563eb' }}>Edit</span>
                </div>
              </div>

              <Section title="Photographers" count={children.photographers.length} color="#0f3460"
                action={<AddBtn label="Photographer" onClick={() => { setNewRole('photographer'); setEditUser(null); }} />}>
                {loading.photographers
                  ? <div style={{ padding: 10, fontSize: 12, color: '#9ca3af' }}>Loading…</div>
                  : children.photographers.length === 0
                    ? <div style={{ padding: 10, fontSize: 11, color: '#9ca3af' }}>None yet</div>
                    : children.photographers.map(p => (
                      <UserCard key={p.id} user={{ ...p, role: 'photographer' }}
                        isSelected={editUser?.id === p.id && !newRole}
                        onClick={() => { setEditUser({ ...p, role: 'photographer' }); setNewRole(null); }}
                        badge={`Q:${p.remaining_quota ?? 0}`} />
                    ))
                }
              </Section>

              <Section title="Events" count={children.events.length} color="#1e3a5f">
                {loading.events
                  ? <div style={{ padding: 10, fontSize: 12, color: '#9ca3af' }}>Loading…</div>
                  : children.events.length === 0
                    ? <div style={{ padding: 10, fontSize: 11, color: '#9ca3af' }}>No events</div>
                    : children.events.map(e => (
                      <EventCard key={e.id} event={e} isSelected={false}
                        onClick={() => pushLevel('event', e)} />
                    ))
                }
              </Section>
            </>
          )}

          {/* EVENT → show event owner + visitors */}
          {currentLevel === 'event' && (
            <>
              <div style={{ padding: '10px', background: '#fdf4ff', borderBottom: '2px solid #e5e7eb' }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#111' }}>{currentData.name}</div>
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                  {currentData.date ? new Date(currentData.date).toLocaleDateString('en-IN') : '—'} · {currentData.venue || '—'}
                </div>
                {currentData.owner_id && (
                  <button onClick={async () => {
                    const u = await get(`/api/superadmin/users/${currentData.owner_id}`);
                    setEditUser(u); setNewRole(null);
                  }} style={{ marginTop: 6, fontSize: 11, background: '#fef9c3', color: '#a16207', border: '1px solid #fde68a', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', fontWeight: 700 }}>
                    Edit Owner: {currentData.owner_name || '—'}
                  </button>
                )}
              </div>

              <Section title="Visitors" count={children.visitors.length} color="#581c87">
                {loading.visitors
                  ? <div style={{ padding: 10, fontSize: 12, color: '#9ca3af' }}>Loading…</div>
                  : children.visitors.length === 0
                    ? <div style={{ padding: 10, fontSize: 11, color: '#9ca3af' }}>No visitors yet</div>
                    : children.visitors.map(v => (
                      <UserCard key={v.id} user={{ ...v, role: 'visitor' }}
                        isSelected={editUser?.id === v.id}
                        onClick={() => { setEditUser({ ...v, role: 'visitor' }); setNewRole(null); }}
                        badge={v.match_count > 0 ? `${v.match_count} photos` : null} />
                    ))
                }
              </Section>
            </>
          )}

        </div>
      </div>

      {/* ── RIGHT CONTENT PANEL ── */}
      <div style={{ flex: 1, overflow: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* New user form */}
        {newRole && (
          <NewUserForm role={newRole}
            onSaved={() => { setNewRole(null); reload(); }}
            onCancel={() => setNewRole(null)} />
        )}

        {/* Edit form */}
        {editUser && !newRole && (
          <EditForm key={editUser.id} user={editUser}
            onSaved={(updated) => {
              setEditUser(updated);
              // Update in-place if it's a dealer in root
              if (currentLevel === 'root' && updated.role === 'dealer') {
                setDealers(ds => ds.map(d => d.id === updated.id ? { ...d, ...updated } : d));
              }
            }} />
        )}

        {/* Visitors table when in event level */}
        {currentLevel === 'event' && !editUser && !newRole && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 13 }}>
              Visitors — {currentData.name}
              <span style={{ background: '#e0e7ff', color: '#4338ca', borderRadius: 10, padding: '1px 8px', fontSize: 11, marginLeft: 8 }}>{children.visitors.length}</span>
            </div>
            <VisitorsTable visitors={children.visitors} loading={loading.visitors}
              selectedId={editUser?.id}
              onSelect={v => { setEditUser({ ...v, role: 'visitor' }); setNewRole(null); }} />
          </div>
        )}

        {/* Default empty state */}
        {!editUser && !newRole && currentLevel !== 'event' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', gap: 8 }}>
            {currentLevel === 'root' && <div style={{ fontSize: 13 }}>← Click a dealer to edit, or drill in with the ↳ link</div>}
            {currentLevel === 'dealer' && <div style={{ fontSize: 13 }}>← Select a photographer or click an event to drill in</div>}
          </div>
        )}
      </div>
    </div>
  );
}
